import React, { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { NumberParam, useQueryParam } from "use-query-params";

import { CheckCircleTwoTone, LogoutOutlined } from "@ant-design/icons";
import { Dropdown, Menu, Space, Spin } from "antd";
import { Header as AntHeader } from "antd/lib/layout/layout";

import { LOGGING_AS_USER_PERM, RESET_PASSWORD_PERM, VIEW_GATES_PAGE_PERM } from "../../permissions";
import { ReactComponent as BackofficeLogo } from "../../static/images/logo.svg";
import { fetchLogout } from "../../store/auth/actions";
import { loginAsUser } from "../../store/gates/actions";
import { RootState } from "../../store/root_reducer";
import { userSelectGate } from "../../store/user/actions";
import { GateSelector, LanguageSelector, ReportSelector } from "../selectors";

import styles from "./styles.module.scss";

export const Header: React.FC = () => {
  const dispatch = useDispatch();

  const [gate, setGate] = useQueryParam("gateId", NumberParam);
  const [userPictureState, setUserPictureState] = useState({ isLoading: true });

  const user = useSelector((state: RootState) => state.User);
  const gates = useSelector((state: RootState) => state.Gates.gates);
  const isLoaded = useSelector((state: RootState) => state.Gates.isLoaded);
  const selectedGate = useSelector((state: RootState) => state.User.selectedGate);

  useEffect(() => {
    if (!gate || gate !== selectedGate.id) {
      setGate(selectedGate.id, "replaceIn");
    }
  }, [dispatch, gate, selectedGate, setGate]);

  const handleLogOut = () => {
    dispatch(fetchLogout());
  };

  const handleSelectGate = (gateId: number) => {
    dispatch(userSelectGate(gateId));
    setGate(gateId, "replaceIn");
    setUserPictureState({ isLoading: true });
  };

  const userMenu = (
    <Menu style={{ borderRadius: "6px" }}>
      {user.availableGates.length > 1 && (
        <>
          {user.availableGates.map((availableGate) => (
            <Menu.Item key={`menuitem-gate-${availableGate.id}`} onClick={() => handleSelectGate(availableGate.id)}>
              {availableGate.id === user.selectedGate.id && <CheckCircleTwoTone twoToneColor="#52c41a" />}
              <span>{availableGate.label}</span>
            </Menu.Item>
          ))}
          <Menu.Divider />
        </>
      )}
      <Menu.Item key="menuitem-logout" onClick={handleLogOut}>
        <LogoutOutlined />
        <span>Log Out</span>
      </Menu.Item>
    </Menu>
  );

  return (
    <AntHeader className={styles.header}>
      <div className={styles.logo}>
        <BackofficeLogo className={styles.image} />
        <div className={styles.title}>Backoffice</div>
      </div>
      <Space className={styles.leftMenu}>
        <LanguageSelector className={styles.language} />
        <ReportSelector />
        {user.permissions.includes(RESET_PASSWORD_PERM) && selectedGate && (
          <a
            target="_popup"
            rel="noreferrer"
            className={styles.password}
            href={`admin/users/partner/${selectedGate.id}/users-password/`}
          >
            Change password
          </a>
        )}

        {user.permissions.includes(LOGGING_AS_USER_PERM) && selectedGate && (
          <a
            target="_blank"
            rel="noreferrer"
            className={styles.password}
            onClick={() => {
              dispatch(loginAsUser(selectedGate.id));
            }}
          >
            Login as user
          </a>
        )}

        {user.permissions.includes(VIEW_GATES_PAGE_PERM) && (
          <GateSelector gates={gates} loading={!isLoaded} value={selectedGate.id} onSelect={handleSelectGate} />
        )}
        {user.isLoaded && (
          <Dropdown arrow overlay={userMenu} trigger={["click"]}>
            <div className={styles.partnerLogo}>
              <Spin
                style={{
                  width: "62px",
                  height: "31px",
                  position: "absolute",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
                spinning={userPictureState.isLoading}
              />
              <img
                key={`user-picture-${selectedGate.id}`}
                style={{ width: "62px", height: "31px", opacity: userPictureState.isLoading ? "0" : "1" }}
                src={`https://pics.avs.io/as_gates/en/62/31/${selectedGate.id}@2x.png`}
                alt={user.username}
                onLoad={() => setUserPictureState({ isLoading: false })}
              />
            </div>
          </Dropdown>
        )}
      </Space>
    </AntHeader>
  );
};
